import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SeasonKind } from "@skladiscenje/contracts";

import { useApiClient } from "../api/ApiClientContext.js";
import { closeSeason } from "../api/seasons.js";
import type { WireSeason } from "../api/types.js";
import { Dialog } from "../components/Dialog.js";

/**
 * "Nova sezona" — closes the currently open season under an operator-chosen
 * name and opens a fresh unnamed one, matching the `SaveGrid` overlay in
 * MainWindow.xaml:73-83 and `SaveCurrentSeason_Click`
 * (MainWindow.xaml.cs:313). No employee PIN here, on purpose — see
 * `apps/server/src/routes/seasons.ts`'s doc comment: legacy's equivalent
 * action never checked one either, so this preserves that (arguably
 * questionable) behaviour rather than inventing a stricter one.
 */
export function CloseSeasonDialog({
  openSeason,
  onClose,
  onClosed,
}: {
  openSeason: WireSeason;
  onClose: () => void;
  onClosed: (opened: WireSeason) => void;
}) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<SeasonKind>("letne");
  const [year, setYear] = useState(() => new Date().getFullYear());

  const mutation = useMutation({
    mutationFn: () => closeSeason(client, { openSeasonId: openSeason.id, name: { kind, year } }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["seasons"] });
      onClosed(result.opened);
    },
  });

  return (
    <Dialog title="Zaključi sezono" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <p>Trenutna odprta sezona bo shranjena pod izbranim imenom, nov vnos bo šel v novo, še neimenovano sezono.</p>

        <label htmlFor="close-season-kind">Vrsta</label>
        <select id="close-season-kind" value={kind} onChange={(e) => setKind(e.target.value as SeasonKind)}>
          <option value="letne">Letne</option>
          <option value="zimske">Zimske</option>
        </select>

        <label htmlFor="close-season-year">Leto</label>
        <input
          id="close-season-year"
          type="number"
          min={2000}
          max={2100}
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
        />

        {mutation.isError && (
          <p role="alert" className="form-error">
            {describeCloseSeasonError(mutation.error)}
          </p>
        )}

        <div className="dialog-actions">
          <button type="button" onClick={onClose}>
            Prekliči
          </button>
          <button type="submit" disabled={mutation.isPending}>
            Shrani
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function describeCloseSeasonError(error: unknown): string {
  if (error && typeof error === "object" && "failure" in error) {
    const failure = (error as { failure: { kind: string; reason?: string } }).failure;
    if (failure.kind === "conflict" && failure.reason === "duplicate-target-label") {
      return "Sezona s tem imenom že obstaja.";
    }
    if (failure.kind === "conflict") return `Zaključek sezone ni uspel (${failure.reason}).`;
  }
  return "Zaključek sezone ni uspel.";
}
